import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CustomerMfeLoader,
  NAVIGATION_GUARD_EVENT,
} from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import type {
  ICustomerMfeNotesCall,
  InitiateChatPayload,
  NavigateTarget,
  NavigationGuardPayload,
} from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import type { EventBus } from '@servicetitan/web-components';
import paths from '@app/routes/paths';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import useBookingRetentionEnabled from '@modules/InboxModule/hooks/useBookingRetentionEnabled';
import useNewAssignedToMeUiEnabled from '@modules/InboxModule/hooks/useNewAssignedToMeUiEnabled';
import {
  listHostTenants,
  resolveTenantId,
  resolveTenantName,
} from '@modules/AuthModule/lib/resolveOrgNodeId';
import { buildContactCenterLDIdentity } from '@modules/FeatureFlagModule/lib/contactCenterLDIdentity';
import useAnvil2ToastBridge from '@modules/ToastModule/useAnvil2ToastBridge';
import environment from '@lib/environment';
import { useInitiateCall } from '@lib/hooks/useInitiateCall';
import { buildCrmApiPath } from '@lib/utils/crmApiPath';
import { openNavigateTarget } from '@lib/utils/openNavigateTarget';
import CustomerMfeLeaveGuard from './CustomerMfeLeaveGuard';
import useBusEvent from './useBusEvent';
import {
  BOOKING_COMPLETED_EVENT,
  BOOK_JOB_REQUESTED_EVENT,
  type BookingCompletedPayload,
  type BookJobRequestedPayload,
  type CustomerMfeSettings,
} from './hostOptions';
import {
  notifyGuardsChanged,
  registerTaskCloseGuard,
  unregisterTaskCloseGuard,
} from './taskCloseGuard';

type CustomerChangedPayload = {
  customerId: string;
  customerName: string;
  orgNodeId: string | null;
} | null;

export type ResolvedCustomer =
  | { id: string; name: string; orgNodeId?: string }
  | undefined;

interface CustomerMfeAdapterProps {
  /** Omit for all-tenants search (Mode 1). Pass the task's tenant-level OrgNode Guid to scope search to one tenant (Modes 2/3). */
  orgNodeId?: string;
  /** Seeds the resolved-customer view directly (Mode 3). Ignored unless orgNodeId is also set. Decides the landing view over phoneNumber, which is still seeded for back-to-search. */
  customerId?: string;
  /**
   * Seeds a phone-scoped search (Mode 2) — e.g. the task's caller ID. Must be the
   * unformatted number (E.164 or bare digits), never a display-masked string: this
   * reaches the agent-inbox BFF as free text, and `(717) 201-2010` matches an order
   * of magnitude more customers there than `+17172012010` does, which defeats the
   * panel's single-match auto-selection (CCPE-1656).
   *
   * Pass the task's caller ID straight through — blank and whitespace-only values
   * are dropped here rather than at each call site, so a task with no caller ID
   * seeds no query at all instead of an empty one.
   *
   * Still sent once a customerId resolves — the panel needs it to have something to
   * search when the CSR uses "back to search" (CCPE-1656 Defect B).
   */
  phoneNumber?: string;
  /**
   * Marketing-campaign attribution for the task's call, for Book Job campaign
   * auto-selection.
   */
  campaignId?: string;
  /**
   * Call SID of the task's active call (`task.attributes.sessionSid`), so a job booked
   * from the MFE links back to the call. The monolith resolves the SID server-side and
   * books unlinked when it can't — absent is a supported outcome, not an error.
   */
  callSid?: string;
  /**
   * The selected task's live call, so a note added during it links to it. Pass it only
   * while the call is live and withdraw it at hang-up; the MFE follows it without
   * remounting. Sent only when the CRM path is configured.
   */
  notesCall?: ICustomerMfeNotesCall;
  /** Fired on resolve/clear. What "a resolved customer" means (task attribute, local state, ...) is the caller's call. */
  onCustomerChanged?: (customer: ResolvedCustomer) => void;
  /**
   * Hold CCP route changes behind the MFE's discard confirmation while Book Job is open
   * (CCPE-2172). Pass true only for the adapter the CSR can currently see: a hidden
   * adapter's confirmation would be invisible, and the router honours one blocker at a time.
   * Ignored with booking retention on: InboxPage's single `InboxLeaveGuard` guards every
   * registered view instead, so pass a `viewKey` there.
   */
  guardNavigation?: boolean;
  /**
   * The task's stable view key. When set, the adapter registers with the task
   * close-guard registry while mounted, so the card's Close and Resume Booking
   * buttons and `InboxLeaveGuard` can see its booking. Omit for views that are
   * not a task card, except the home view, which passes `HOME_VIEW_KEY` with
   * booking retention on.
   */
  viewKey?: string;
  /** Called when the CSR confirms a Close from the card's button. */
  onCloseConfirmed?: () => void;
  /** Called when the MFE enters or leaves a booking, whether or not this view is visible. */
  onGuardedChange?: (guarded: boolean) => void;
  /**
   * Called when the CSR clicks Book Job in customer-mfe. Passing it makes the
   * host start the booking instead of customer-mfe opening Book Job here (the
   * Inbox home view, with the new Assigned to Me column).
   */
  onBookJobRequested?: (payload: BookJobRequestedPayload) => void;
  /** Seeds the selected location with the customer. Ignored without a customer seed. */
  locationId?: string;
  /** Opens Book Job at mount for the seeded customer. Ignored without a customer seed. */
  openBookJob?: boolean;
  /**
   * The seeded customer's name, for the discard prompts until customer-mfe
   * reports one. Ignored without a customer seed.
   */
  customerName?: string;
  /** Called once a job booked in this view has settled. */
  onBookingCompleted?: (payload: BookingCompletedPayload) => void;
  /**
   * The booking has no call to link (a manual booking card), so a booked job
   * shows customer-mfe's plain "Job Booked" toast, not the call-link warning.
   */
  noCallToLink?: boolean;
}

type BookJobSettingsInput = {
  hostStartsBookJob: boolean;
  canSeedCustomer: boolean;
  locationId?: string;
  openBookJob: boolean;
  customerName?: string;
};

/**
 * Who starts Book Job, and the Book Job seed. customer-mfe reads the seed once,
 * at mount, and only with a customer seed.
 */
const bookJobSettings = ({
  hostStartsBookJob,
  canSeedCustomer,
  locationId,
  openBookJob,
  customerName,
}: BookJobSettingsInput): Partial<CustomerMfeSettings> => ({
  ...(hostStartsBookJob ? { hostStartsBookJob: true } : {}),
  ...(canSeedCustomer && locationId !== undefined ? { locationId } : {}),
  ...(canSeedCustomer && openBookJob ? { openBookJob: true } : {}),
  ...(canSeedCustomer && customerName ? { customerName } : {}),
});

/**
 * The notes settings: the CRM path when this environment configures one, and
 * the live call only alongside it. Without the path the MFE turns notes off, so
 * a call on its own has nowhere to go.
 */
const notesSettings = (
  notesCall: ICustomerMfeNotesCall | undefined,
): Partial<CustomerMfeSettings> => {
  // Built per call, not at module load, so a test's environment mock applies.
  const crmApiPath = buildCrmApiPath(
    environment.API_URL,
    environment.CRM_BFF_PREFIX,
  );
  if (crmApiPath === undefined) {
    return {};
  }
  return notesCall === undefined ? { crmApiPath } : { crmApiPath, notesCall };
};

/**
 * Mounts customer-mfe. Callers bridge the resolve/clear signal onto whatever
 * "current customer" concept they own (TasksModule for a task, local state
 * for the home view) via onCustomerChanged.
 */
const CustomerMfeAdapter = ({
  orgNodeId,
  customerId,
  phoneNumber,
  campaignId,
  callSid,
  notesCall,
  onCustomerChanged,
  guardNavigation = false,
  viewKey,
  onCloseConfirmed,
  onGuardedChange,
  onBookJobRequested,
  locationId,
  openBookJob = false,
  customerName,
  onBookingCompleted,
  noCallToLink = false,
}: CustomerMfeAdapterProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleInitiateCall = useInitiateCall();
  const busRef = useRef<EventBus | null>(null);
  const [isBookingGuarded, setIsBookingGuarded] = useState(false);
  const hasUser = Boolean(user);
  const isRetentionEnabled = useBookingRetentionEnabled();
  // With the new Assigned to Me column every card asks "Dismiss this booking?"
  const isNewAssignedToMeUiEnabled = useNewAssignedToMeUiEnabled();
  // Mode 3 needs both halves: a lone customerId with no tenant scope falls back to
  // Mode 1 in the MFE's own seed derivation.
  const canSeedCustomer = orgNodeId !== undefined && customerId !== undefined;
  // the registry's source of truth, written only by the navigation-guard
  // handler: copying state over it on render could briefly undo that write
  const isBookingGuardedRef = useRef(false);
  const onCloseConfirmedRef = useRef(onCloseConfirmed);
  onCloseConfirmedRef.current = onCloseConfirmed;
  const onGuardedChangeRef = useRef(onGuardedChange);
  onGuardedChangeRef.current = onGuardedChange;
  // Older loaders subscribe to customer changes once, at mount. A handler built
  // from the first render's props would write a customer to the task id from
  // before a late-data re-key, so the loader gets one stable handler that reads
  // the latest callback.
  const onCustomerChangedRef = useRef(onCustomerChanged);
  onCustomerChangedRef.current = onCustomerChanged;
  // the resolved customer's name, for the host's discard prompts; a seeded
  // customer's name until customer-mfe reports one
  const customerNameRef = useRef<string | undefined>(
    canSeedCustomer ? customerName : undefined,
  );

  // customer-mfe fires anvil2 `toast.*()` (global `hammer-toast` window event),
  // but its shadow-DOM `<Toaster>` silences itself because the host's light-DOM
  // `dialog-toaster` wins anvil2's "first toaster" check (CCPE-1317). Bridge the
  // event to CCP's native ToastModule so MFE toasts actually render.
  useAnvil2ToastBridge(true);

  const handleCustomerChanged = useCallback(
    (payload: CustomerChangedPayload) => {
      customerNameRef.current = payload?.customerName;
      onCustomerChangedRef.current?.(
        payload
          ? {
              id: payload.customerId,
              name: payload.customerName,
              orgNodeId: payload.orgNodeId ?? undefined,
            }
          : undefined,
      );
    },
    [],
  );

  const handleInitiateChat = useCallback(
    (payload: InitiateChatPayload) => {
      // Mirrors handleInitiateCall's bail-on-unresolvable precedent: don't land the
      // CSR on a tenant-less Messages page (REQ-TXT-CTC-004 AC-3).
      const tenantId = resolveTenantId(
        user?.org.tenants ?? [],
        payload.orgNodeId,
      );
      if (!tenantId) {
        return;
      }
      const seed = new URLSearchParams({
        customerId: String(payload.customerId),
        orgNodeId: payload.orgNodeId,
        contact: payload.contact,
      });
      navigate({ pathname: paths.inboxTexts, search: seed.toString() });
    },
    [navigate, user?.org.tenants],
  );

  const handleNavigate = useCallback(
    (target: NavigateTarget) => {
      openNavigateTarget(target, user?.org.tenants ?? []);
    },
    [user?.org.tenants],
  );

  // Tracked here, not in CustomerMfeLeaveGuard: the guard only mounts while this adapter
  // is visible, and would miss a `guarded: true` emitted while its task was in the background.
  useEffect(() => {
    const bus = busRef.current;
    if (!bus) {
      return;
    }
    const handleGuardChanged = ({ guarded }: NavigationGuardPayload) => {
      // set before notifying, so registry subscribers read the new value
      isBookingGuardedRef.current = guarded;
      setIsBookingGuarded(guarded);
      onGuardedChangeRef.current?.(guarded);
      notifyGuardsChanged();
    };
    bus.on(NAVIGATION_GUARD_EVENT, handleGuardChanged);
    return () => {
      bus.off(NAVIGATION_GUARD_EVENT, handleGuardChanged);
      // The MFE sends nothing when it unmounts, so a booking left open would
      // outlive this loader (a sign-out and back in): drop it with the bus.
      // The next MFE reports its own state once it mounts.
      isBookingGuardedRef.current = false;
      setIsBookingGuarded(false);
    };
  }, [hasUser]); // the loader (and so the bus) only mounts once there is a user

  // the loader (and so the bus) only mounts once there is a user
  useBusEvent(busRef, BOOKING_COMPLETED_EVENT, onBookingCompleted, hasUser);
  useBusEvent(busRef, BOOK_JOB_REQUESTED_EVENT, onBookJobRequested, hasUser);

  // Registered for the adapter's whole mounted life, hidden or not, so a Close
  // from a background card still sees its booking.
  useEffect(() => {
    const bus = busRef.current;
    // no bus means the loader, and so the MFE, is not mounted yet
    if (!viewKey || !bus) {
      return;
    }
    const token = registerTaskCloseGuard(viewKey, {
      bus,
      isBookingGuarded: () => isBookingGuardedRef.current,
      getCustomerName: () => customerNameRef.current,
      onCloseConfirmed: () => onCloseConfirmedRef.current?.(),
    });
    return () => unregisterTaskCloseGuard(token);
  }, [viewKey, hasUser]);

  if (!user) {
    return null;
  }

  // Blank or whitespace-only caller IDs seed nothing — guarded here rather than at
  // each call site so every caller (task view, cross-tenant task view) is covered.
  const hasPhoneQuery =
    phoneNumber !== undefined && phoneNumber.trim().length > 0;

  // The MFE takes the tenant's label as a seed separate from the scope Guid; without
  // it the search tab's chip reads "All Tenants" even though the request carries
  // orgNodeIds and is correctly scoped (CCPE-1656 Defect C).
  const tenantName = resolveTenantName(user.org.tenants, orgNodeId);

  const settings: CustomerMfeSettings = {
    mfeUrl: environment.CUSTOMER_MFE_URL,
    agentInboxApiPath: `${environment.API_URL}/bffproxy/agent-inbox`,
    host: 'contactcenter',
    userId: user.id,
    accountId: user.accountId,
    contactCenterId: user.contactCenterId,
    networkOrgId: user.org.id,
    featureFlagIdentity: buildContactCenterLDIdentity(user),
    ...(orgNodeId !== undefined ? { orgNodeId } : {}),
    ...(tenantName !== undefined ? { tenantName } : {}),
    // The tenants this host applies to. The MFE limits the all-tenants search
    // and Create New to the ones the user is also entitled to. A tenant-scoped
    // search ignores it.
    tenants: listHostTenants(user.org.tenants),
    ...(canSeedCustomer ? { customerId } : {}),
    // Still requires an orgNodeId — a phone query with no tenant scope is a
    // Mode 1 all-tenants search, not what the caller asked for. Deliberately
    // NOT suppressed once a customer resolves: Mode 3 decides the landing view
    // on its own (customerId wins there), and the panel needs the number to have
    // anything to show behind "back to search" (CCPE-1656 Defect B).
    ...(orgNodeId !== undefined && hasPhoneQuery
      ? { initialQuery: phoneNumber, initialTab: 'suggested' as const }
      : {}),
    ...(campaignId !== undefined ? { campaignId } : {}),
    ...(callSid !== undefined ? { callSid } : {}),
    ...notesSettings(notesCall),
    ...bookJobSettings({
      hostStartsBookJob: onBookJobRequested !== undefined,
      canSeedCustomer,
      locationId,
      openBookJob,
      customerName,
    }),
    ...(isNewAssignedToMeUiEnabled ? { dismissBookingConfirm: true } : {}),
    ...(noCallToLink ? { noCallToLink: true } : {}),
  };

  return (
    // w-100/h-100: the loader's web-component host has no intrinsic size, and
    // customer-mfe's own shell is height:100% all the way down (app.module.css) —
    // without an explicit height here that chain resolves against auto and collapses.
    <div className='w-100 h-100'>
      <CustomerMfeLoader
        ref={busRef}
        settings={settings}
        onCustomerChanged={handleCustomerChanged}
        onInitiateCall={handleInitiateCall}
        onInitiateChat={handleInitiateChat}
        onNavigate={handleNavigate}
      />
      {guardNavigation && !isRetentionEnabled && (
        <CustomerMfeLeaveGuard busRef={busRef} guarded={isBookingGuarded} />
      )}
    </div>
  );
};

export default CustomerMfeAdapter;
